import { useEffect, useState } from 'react';
import { FiEdit2 } from 'react-icons/fi';
import ReturnPanel from '../../components/ReturnPanel';
import { apiDownloadInvoice } from '../../api/orders';

const formatOrderMoney = (amount, order) => new Intl.NumberFormat(
  order.orderCurrencyCode === 'INR' ? 'en-IN' : 'en-US',
  { style: 'currency', currency: order.orderCurrencyCode || 'INR' }
).format(Number(amount) || 0);

const OrderDetailDrawer = ({ order, onClose, onReturnSave, onStatusChange }) => {
  const [downloading, setDownloading] = useState(false);
  const [editingShipping, setEditingShipping] = useState(false);
  const [savingShipping, setSavingShipping] = useState(false);
  const [shipping, setShipping] = useState({ courier: '', trackingNumber: '' });

  useEffect(() => {
    setEditingShipping(false);
    setShipping({
      courier: order?.courier || '',
      trackingNumber: order?.trackingNumber || '',
    });
  }, [order]);

  if (!order) return null;

  const canDownloadInvoice =
    order.status !== 'Cancelled' && order.paymentStatus !== 'Failed';

  const handleDownloadInvoice = async () => {
    setDownloading(true);
    try {
      await apiDownloadInvoice(order.orderId || order.id);
    } catch { /* API client displays the error toast. */ } finally {
      setDownloading(false);
    }
  };

  const saveShipping = async (event) => {
    event.preventDefault();
    if (!shipping.courier.trim() || !shipping.trackingNumber.trim()) return;
    setSavingShipping(true);
    const updated = await onStatusChange(order.orderId || order.id, 'shipped', {
      courier: shipping.courier.trim(),
      trackingNumber: shipping.trackingNumber.trim(),
    });
    setSavingShipping(false);
    if (updated) setEditingShipping(false);
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex justify-end"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white h-full overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-black">
            Order #{order.orderId || order.id}
          </h3>
          <button
            onClick={onClose}
            className="text-black hover:text-black text-xl"
          >
            ×
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <p className="text-sm text-black">Customer</p>
            <p className="font-medium">{order.user?.name || 'N/A'}</p>
            <p className="text-sm text-black">{order.user?.email}</p>
          </div>
          <div>
            <p className="text-sm text-black">Shipping Address</p>
            <p className="text-sm">{order.shippingAddress || 'N/A'}</p>
          </div>
          {order.status === 'shipped' && (
            <section className="rounded-lg border border-orange-200 bg-orange-50 p-3">
              <div className="flex items-center justify-between gap-3">
                <h4 className="text-sm font-semibold text-black">Shipment Details</h4>
                {!editingShipping && (
                  <button
                    type="button"
                    onClick={() => setEditingShipping(true)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-orange-700"
                  >
                    <FiEdit2 /> Edit
                  </button>
                )}
              </div>
              {editingShipping ? (
                <form onSubmit={saveShipping} className="mt-3 space-y-3">
                  <label className="block text-xs font-medium text-gray-700">
                    Courier
                    <input required maxLength={100} value={shipping.courier} onChange={(e) => setShipping((current) => ({ ...current, courier: e.target.value }))} className="mt-1 w-full rounded border bg-white px-3 py-2 text-sm" />
                  </label>
                  <label className="block text-xs font-medium text-gray-700">
                    Tracking Number
                    <input required maxLength={100} value={shipping.trackingNumber} onChange={(e) => setShipping((current) => ({ ...current, trackingNumber: e.target.value }))} className="mt-1 w-full rounded border bg-white px-3 py-2 text-sm" />
                  </label>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setEditingShipping(false)} disabled={savingShipping} className="rounded border bg-white px-3 py-1.5 text-xs">Cancel</button>
                    <button type="submit" disabled={savingShipping} className="rounded bg-orange-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">{savingShipping ? 'Saving...' : 'Update Shipping'}</button>
                  </div>
                </form>
              ) : (
                <dl className="mt-2 space-y-1 text-sm">
                  <div className="flex justify-between gap-3"><dt className="text-gray-600">Courier</dt><dd className="font-medium">{order.courier || '—'}</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-gray-600">Tracking</dt><dd className="break-all font-mono font-medium">{order.trackingNumber || '—'}</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-gray-600">Dispatched</dt><dd>{order.dispatchedAt ? new Date(order.dispatchedAt).toLocaleString() : '—'}</dd></div>
                </dl>
              )}
            </section>
          )}
          <div>
            <p className="text-sm text-black mb-2">
              Items ({order.items?.length || 0})
            </p>
            {(order.items || []).map((item, i) => (
              <div
                key={i}
                className="py-2 border-b border-gray-100 text-sm"
              >
                <div className="flex justify-between">
                  <div>
                    <p className="font-medium">{item.title}</p>
                    {item.sku && <p className="break-all text-xs text-gray-600">SKU: {item.sku}</p>}
                    <p className="text-xs text-black">Qty: {item.quantity}</p>
                  </div>
                  <p className="font-medium">
                    {formatOrderMoney(parseFloat(item.price) * item.quantity, order)}
                  </p>
                </div>
                {item.returnRequest && (
                  <ReturnPanel
                    returnRequest={{ ...item.returnRequest, order }}
                    onSave={onReturnSave}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="border-t pt-3">
            <div className="flex justify-between text-sm">
              <span>Subtotal</span>
              <span>{formatOrderMoney(order.total, order)}</span>
            </div>
            {Number(order.taxAdded) > 0 && <div className="flex justify-between text-sm"><span>Tax</span><span>{formatOrderMoney(order.taxAdded, order)}</span></div>}
            {order.discount > 0 && (
              <div className="flex justify-between text-sm text-green-600">
                <span>Discount</span>
                <span>-{formatOrderMoney(order.discount, order)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold mt-1">
              <span>Total</span>
              <span>
                {order.paymentMethod === 'paypal' && order.paypalAmountUsd && order.orderCurrencyCode !== 'USD' ? (
                  <>
                    ${parseFloat(order.paypalAmountUsd).toFixed(2)}{' '}
                    <span className="text-xs text-black align-middle">USD</span>
                    <span className="block text-xs font-normal text-black text-right">
                      {formatOrderMoney(order.finalAmount || order.total, order)}
                    </span>
                  </>
                ) : (
                  <>{formatOrderMoney(order.finalAmount || order.total, order)}</>
                )}
              </span>
            </div>
            {order.paymentMethod === 'paypal' && (
              <div className="mt-2 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                Paid via PayPal{order.paypalAmountUsd ? ` ($${parseFloat(order.paypalAmountUsd).toFixed(2)} USD)` : ' (charged in USD)'}
                {order.orderCurrencyCode !== 'USD' && order.paypalExchangeRate ? ` • Rate: 1 INR = ${parseFloat(order.paypalExchangeRate).toFixed(4)} USD` : ''}
                <br />Txn: {order.razorpayPaymentId || 'pending'}
              </div>
            )}
          </div>
          {canDownloadInvoice && (
            <button
              onClick={handleDownloadInvoice}
              disabled={downloading}
              className="w-full mt-4 bg-orange-50 border border-orange-200 text-orange-600 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-orange-100 transition disabled:opacity-50"
            >
              {downloading ? 'Downloading...' : '📄 Download Invoice'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderDetailDrawer;
